import { useSuspenseQuery } from '@tanstack/react-query'
import { useSetAccountPreferences } from '~/hooks'
import { userPreferencesQueryOptions } from '~/queries'
import { Popover, PopoverContent, PopoverTrigger, showDialog, useIsMobile } from '@bpinternal/ui-kit-next'
import { AnnouncementDialog } from './AnnouncementDialog'
import { Announcement } from '../types'
import { ReactNode } from 'react'
import { formatRelativeDate } from '@bpinternal/shared'

type AnnouncementPopoverProps = {
  announcements: Announcement[]
  workspaceId: string
  botId: string
  children: ReactNode
}

export const AnnouncementPopover = ({ announcements, workspaceId, botId, children }: AnnouncementPopoverProps) => {
  const { data: seenAnnouncementIds } = useSuspenseQuery(userPreferencesQueryOptions({ path: 'seenAnnouncementIds' }))
  const { mutate: setPreferences } = useSetAccountPreferences()

  const isMobile = useIsMobile()

  const sortedAnnouncements = [...announcements].sort(
    (a, b) => new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime()
  )

  const onAnnouncementItemClick = (announcement: Announcement) => {
    setPreferences({
      path: 'seenAnnouncementIds',
      value: [...seenAnnouncementIds, announcement.id],
    })
    showDialog(
      ({ close }) => (
        <AnnouncementDialog close={close} announcement={announcement} workspaceId={workspaceId} botId={botId} />
      ),
      {
        layout: 'content-only',
        size: 'max-w-lg',
      }
    )
  }

  return (
    <Popover>
      <PopoverTrigger>{children}</PopoverTrigger>
      <PopoverContent
        collisionPadding={20}
        className="max-h-[450px] w-[320px] overflow-auto bg-neutral-50 p-0 sm:w-[400px] "
      >
        {sortedAnnouncements.map((announcement, index) => {
          const hasBeenSeen = seenAnnouncementIds.includes(announcement.id)

          if (index === 0) {
            return (
              <VerticalAnnoucementItem
                key={announcement.id}
                announcement={announcement}
                hasBeenSeen={hasBeenSeen}
                relativeDate={formatRelativeDate(announcement.releaseDate)}
                onClick={onAnnouncementItemClick}
              />
            )
          }
          if (isMobile) {
            return (
              <VerticalAnnoucementItem
                key={announcement.id}
                announcement={announcement}
                hasBeenSeen={hasBeenSeen}
                relativeDate={formatRelativeDate(announcement.releaseDate)}
                onClick={onAnnouncementItemClick}
              />
            )
          }
          return (
            <HorizontalAnnoucementItem
              key={announcement.id}
              announcement={announcement}
              hasBeenSeen={hasBeenSeen}
              relativeDate={formatRelativeDate(announcement.releaseDate)}
              onClick={onAnnouncementItemClick}
            />
          )
        })}
      </PopoverContent>
    </Popover>
  )
}

const VerticalAnnoucementItem = ({
  hasBeenSeen,
  relativeDate,
  announcement,
  onClick,
}: {
  hasBeenSeen: boolean
  relativeDate: string
  announcement: Announcement
  onClick: (announcement: Announcement) => void
}) => {
  return (
    <div
      key={announcement.id}
      className="flex cursor-pointer flex-col gap-4 border-b p-4 hover:bg-neutral-50"
      onClick={() => onClick(announcement)}
    >
      <div className="flex items-center gap-2">
        {!hasBeenSeen && <div className="size-2 flex-shrink-0 rounded-full bg-red-600"></div>}
        <div className="font-medium">{announcement.title}</div>
      </div>
      <div className="text-sm text-muted-foreground">{announcement.description}</div>
      <img className="w-full rounded-xl" src={announcement.asset}></img>
      <div className="text-sm text-muted-foreground">{relativeDate}</div>
    </div>
  )
}

const HorizontalAnnoucementItem = ({
  hasBeenSeen,
  relativeDate,
  announcement,
  onClick,
}: {
  hasBeenSeen: boolean
  relativeDate: string
  announcement: Announcement
  onClick: (announcement: Announcement) => void
}) => {
  return (
    <div
      key={announcement.id}
      className="flex cursor-pointer flex-col gap-2 border-b p-4 hover:bg-neutral-50"
      onClick={() => onClick(announcement)}
    >
      <div className="flex gap-2">
        <div className="flex flex-1 flex-col gap-2">
          <div className="flex items-center gap-2">
            {!hasBeenSeen && <div className="size-2 flex-shrink-0 rounded-full bg-red-600"></div>}
            <div className="font-medium">{announcement.title}</div>
          </div>
          <div className="text-sm text-muted-foreground">{announcement.description}</div>
        </div>
        <img className="h-20 w-32 rounded-xl object-cover" src={announcement.asset}></img>
      </div>
      <div className="text-sm text-muted-foreground">{relativeDate}</div>
    </div>
  )
}
